import { DeleteOutline } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
  Typography,
} from "@mui/material";
import { Theme, useMediaQuery } from "@mui/system";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { Attachment, Initiative, UnsavedAttachment } from "aint-types";
import dayjs from "dayjs";
import { PropsWithChildren, useEffect, useState } from "react";
import {
  FieldValues,
  FormProvider,
  Path,
  useForm,
  useFormContext,
  UseFormSetError,
  useFormState,
} from "react-hook-form";

import { ReportImpactButton } from "../../../components/report-title-card/report-title-card";
import {
  validateFormFields,
  validateFormFieldsForDraft,
} from "../../../form/validate-form-fields";
import { EditInitiativeDialogContent } from "./edit-initiative-dialog-content";

export type EditInitiativeDialogProps = {
  /** The title of the dialog */
  title: string;
  /** Show Loading spinner instead of form*/
  isLoading?: boolean;
  /** Show Loading spinner on top of form*/
  isSaving?: boolean;
  /** Should the inter oe region field be shown */
  showInterOeRegionField: boolean;
  /** Should the business unit field be shown */
  showBusinessUnitField?: boolean;
  /** Should the category field be shown */
  showCategoryField: boolean;
  /** Should the OE Program fields be shown */
  showOeProgramFields: boolean;
  /** Should the Elevate option be shown for the implementation program field */
  showElevateImplProgramOption: boolean;
  /** Should the line of business options for the AZ Partners OE be shown */
  showExtendedLobOptions?: boolean;
  /** The initiative being edited. */
  initiative?: Partial<Initiative>;
  /** The supporting documents of the initiative being edited */
  supportingDocuments?: (UnsavedAttachment | Attachment)[];
  isCreate: boolean;
  usersWithAccessToOE: string[];
};

export type EditInitiativeDialogEvents =
  | "edit-dialog:submit"
  | "edit-dialog:submit-draft"
  | "edit-dialog:publish"
  | "edit-dialog:close"
  | "edit-dialog:close-with-changes"
  | "edit-dialog:delete"
  | "edit-dialog:report-impact"
  | "edit-dialog:report-impact-with-changes"
  | "edit-dialog:upload-document-button-click"
  | "edit-dialog:delete-document-button-click";

export type EditInitiativeDialogEventPayload = {
  initiative?: Initiative;
  files?: FileList;
  fileId?: string;
  fileIndex?: number;
};

export type EditInitiativeDialogFireEventType =
  | ((
      event: EditInitiativeDialogEvents,
      payload?: EditInitiativeDialogEventPayload
    ) => void)
  | undefined;

export const EditInitiativeDialog: PimoReactComponent<
  EditInitiativeDialogProps,
  EditInitiativeDialogEvents,
  EditInitiativeDialogEventPayload
> = (props) => {
  const {
    fireEvent,
    initiative,
    supportingDocuments,
    title,
    usersWithAccessToOE,
    isLoading = false,
    isSaving = false,
  } = props;
  const formName = "edit-initiative-dialog-form";

  const formMethods = useForm<Partial<Initiative>>({
    defaultValues: {
      ...initiative,
    },
  });

  useEffect(() => {
    formMethods.reset(initiative);
  }, [formMethods, initiative]);

  const onSubmit = (data: FieldValues) => {
    formMethods.clearErrors();

    const hasError = validate(
      data as Initiative,
      props.showCategoryField ?? false,
      false,
      formMethods.setError
    );

    if (hasError) {
      return;
    }

    return fireEvent?.("edit-dialog:submit", {
      initiative: data as Initiative,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <DialogWrapper
        title={title}
        showReportImpactButton={
          !!initiative?.id &&
          initiative?.draft !== true &&
          initiative?.initiativePhase !== "L0 - Idea" &&
          initiative?.updateStatus !== "ended"
        }
        isCategoryMandatory={props.showCategoryField ?? false}
        formName={formName}
        fireEvent={fireEvent}
        isLoading={isLoading}
        isSaving={isSaving}
        lastUpdated={initiative?.lastUpdated}
      >
        <EditInitiativeDialogContent
          activeInitiativeUID={initiative?.uid ?? "new"}
          isCreate={props.isCreate}
          formName={formName}
          onSubmit={onSubmit}
          onInvalid={() => {
            onSubmit(formMethods.getValues());
          }}
          showInterOeRegionField={props.showInterOeRegionField ?? false}
          showBusinessUnitField={props.showBusinessUnitField ?? false}
          showCategoryField={props.showCategoryField ?? false}
          showOeProgramFields={props.showOeProgramFields ?? false}
          showExtendedLobOptions={props.showExtendedLobOptions ?? false}
          showElevateImplProgramOption={
            props.showElevateImplProgramOption ?? false
          }
          usersWithAccessToOE={usersWithAccessToOE}
          fireEvent={fireEvent}
          supportingDocuments={supportingDocuments ?? []}
        />
      </DialogWrapper>
    </FormProvider>
  );
};

const DialogHeader = styled(Box)(({ theme }) => ({
  marginLeft: "40px",
  display: "flex",
  justifyContent: "space-between",
  paddingRight: theme.spacing(2),
}));

const DialogWrapper = ({
  title,
  formName,
  fireEvent,
  isLoading,
  isSaving,
  children,
  lastUpdated,
  showReportImpactButton,
  isCategoryMandatory,
}: PropsWithChildren<{
  title: string;
  formName: string;
  showReportImpactButton: boolean;
  isCategoryMandatory: boolean;
  fireEvent: EditInitiativeDialogFireEventType;
  isLoading: boolean;
  isSaving: boolean;
  lastUpdated?: string;
}>) => {
  const { getValues, setError, clearErrors } = useFormContext();

  const [isDirty, setIsDirty] = useState(false);
  const isLargeScreen = useMediaQuery("(max-width:1000px)");
  const isDraft: boolean | undefined = getValues().draft;

  const handleClose = () => {
    if (isDirty) {
      fireEvent?.("edit-dialog:close-with-changes");
      return;
    }

    fireEvent?.("edit-dialog:close");
  };

  const onPublishClick = () => {
    clearErrors();

    const data = getValues() as Initiative;
    const hasError = validate(data, isCategoryMandatory, false, setError);

    if (hasError) {
      return;
    }

    return fireEvent?.("edit-dialog:publish", { initiative: data });
  };

  const onSaveAsDraftClick = () => {
    clearErrors();

    const data = getValues() as Initiative;
    const hasError = validate(data, isCategoryMandatory, true, setError);

    if (hasError) {
      return;
    }

    return fireEvent?.("edit-dialog:submit-draft", { initiative: data });
  };

  if (isLoading) {
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={true}
      >
        <CircularProgress
          size={50}
          sx={{ color: (theme: Theme) => theme.palette.action }}
        />
      </Backdrop>
    );
  }

  return (
    <Dialog
      sx={{
        margin: "50px",
      }}
      PaperProps={{ sx: { borderRadius: "12px" } }}
      fullWidth={true}
      maxWidth={isLargeScreen ? "md" : "lg"}
      fullScreen
      open={true}
      onClose={handleClose}
      scroll="paper"
    >
      <IsDirtyWatcher setIsDirty={setIsDirty} />
      {isSaving && (
        <Backdrop sx={{ color: "#fff", zIndex: 99999 }} open={true}>
          <CircularProgress
            size={50}
            sx={{ color: (theme: Theme) => theme.palette.action }}
          />
        </Backdrop>
      )}

      <DialogHeader>
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "22px",
          }}
        >
          {title}
          {/* TODO: Add due date */}
        </DialogTitle>
        <Box marginY="auto">
          {showReportImpactButton && (
            <ReportImpactButton
              onClick={() => {
                if (isDirty) {
                  fireEvent?.("edit-dialog:report-impact-with-changes");
                  return;
                }
                fireEvent?.("edit-dialog:report-impact");
              }}
            />
          )}
          <IconButton aria-label="close" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogHeader>
      <DialogContent sx={{ padding: 0 }} dividers={true}>
        {children}
      </DialogContent>
      <DialogActions>
        <EditInitiativeActions
          fireEvent={fireEvent}
          handleClose={handleClose}
          onPublishClick={onPublishClick}
          onSaveAsDraftClick={onSaveAsDraftClick}
          formName={formName}
          isDraft={isDraft}
          lastUpdated={lastUpdated}
        />
      </DialogActions>
    </Dialog>
  );
};

function EditInitiativeActions({
  fireEvent,
  handleClose,
  onPublishClick,
  onSaveAsDraftClick,
  formName,
  isDraft,
  lastUpdated,
}: {
  formName: string;
  fireEvent: EditInitiativeDialogFireEventType;
  handleClose: () => void;
  onPublishClick: () => void;
  onSaveAsDraftClick: () => void;
  isDraft?: boolean;
  lastUpdated?: string;
}) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      sx={{ padding: "20px 40px", width: "100%" }}
    >
      <Button
        startIcon={<DeleteOutline />}
        onClick={() => fireEvent?.("edit-dialog:delete")}
        variant="outlined"
        sx={(theme) => ({
          borderRadius: "4px",
          borderColor: theme.palette.primary.main,
          color: theme.palette.primary.main,
        })}
      >
        Delete
      </Button>
      <Box display="flex" alignItems="center" gap="8px">
        {lastUpdated && (
          <Typography fontSize="12px" fontWeight="700" marginRight="12px">
            Last update: {dayjs(lastUpdated).format("YYYY/MM/DD")}
          </Typography>
        )}
        <Button
          sx={(theme) => ({
            borderRadius: "4px",
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          })}
          onClick={handleClose}
          variant="outlined"
        >
          Cancel
        </Button>
        {isDraft === true && (
          <Button
            variant="contained"
            onClick={onPublishClick}
            sx={{
              borderRadius: "4px",
              color: "#FFF",
            }}
          >
            Publish
          </Button>
        )}
        {isDraft !== false && (
          <Button
            variant="contained"
            form={formName}
            onClick={onSaveAsDraftClick}
            sx={{ borderRadius: "4px", color: "#FFF" }}
          >
            Save as Draft
          </Button>
        )}
        <Button
          variant="contained"
          type="submit"
          form={formName}
          sx={{ borderRadius: "4px", color: "#FFF" }}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
}

const validate = (
  data: Initiative,
  isCategoryMandatory: boolean,
  saveAsDraft: boolean,
  setError: UseFormSetError<Partial<Initiative>>
) => {
  const errors = saveAsDraft
    ? validateFormFieldsForDraft(data)
    : validateFormFields(data, isCategoryMandatory);

  let hasError = false;

  for (const [key, value] of Object.entries(errors ?? {})) {
    if (value) {
      hasError = true;
      setError(key as Path<Initiative>, value);
    }
  }

  return hasError;
};

// A Component without a ui (returns null), to watch for when the form changes to a dirty state.
// Updates the state of the DialogWrapper. This reduces the amount of rerenders in the form and solves
// the form scrolling up when changing an impact field.
const IsDirtyWatcher = ({
  setIsDirty,
}: {
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { dirtyFields } = useFormState();

  useEffect(() => {
    setIsDirty(Object.keys(dirtyFields).length > 0);
  });

  return null;
};
